import React from 'react';
import {
  type BenefitConfig,
  type Category,
  type CurrentPolicyContext,
  CalculationMethod,
  BenefitType,
} from '../../types';
import { CURRENCY_SYMBOLS } from '../../constants';
import { formatCurrency, formatWageroll } from '../../utils/formatters';
import { HumanRisksLogo } from '../ui/HumanRisksLogo';

interface QuoteOutputProps {
  context: CurrentPolicyContext;
}

export const QuoteOutput: React.FC<QuoteOutputProps> = ({ context }) => {
  const currency = CURRENCY_SYMBOLS[context.policy_parameters.currency];

  // A4 Dimensions (210mm x 297mm)
  const pageClass =
    'w-[210mm] h-[297mm] bg-white relative overflow-hidden flex flex-col shadow-2xl mb-8 pdf-page font-sans text-slate-800';

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'TBA';
    return new Date(dateStr)
      .toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      .toUpperCase();
  };

  // --- SUB-COMPONENTS ---

  const HeaderPage1 = () => (
    <div className="px-12 pt-8 pb-3">
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col gap-1">
          <HumanRisksLogo className="h-8" />
          <span className="text-[8px] font-bold text-slate-400 tracking-[0.2em] uppercase mt-1">
            Specialist Underwriting Division
          </span>
        </div>
        <div className="text-right">
          <div className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1">
            Capital Backed By
          </div>
          <div className="text-[10px] font-bold text-hr-indigo uppercase tracking-tight">
            Lombard Insurance Company Ltd
          </div>
          <div className="text-[8px] font-bold text-slate-400 uppercase mt-0.5">
            Established Authority | FSP 1596
          </div>
        </div>
      </div>

      {/* Main Title Block */}
      <div className="bg-hr-pink py-4 px-6 flex justify-between items-center rounded-lg shadow-md">
        <div>
          <h1 className="text-xl font-black text-white tracking-tight uppercase leading-none">
            Group Personal Accident
          </h1>
          <p className="text-[9px] font-bold text-slate-100 uppercase tracking-widest mt-1.5">
            Formal Financial Proposal | LHRGPA4
          </p>
        </div>
        <div className="text-right">
          <div className="flex gap-6">
            <div>
              <div className="text-[8px] font-bold text-slate-200 uppercase mb-1">Quote Date</div>
              <div className="text-[10px] font-bold text-white">
                {formatDate(context.quote_meta.createdDate)}
              </div>
            </div>
            <div>
              <div className="text-[8px] font-bold text-slate-200 uppercase mb-1">Reference</div>
              <div className="text-[10px] font-bold text-white">{context.quote_meta.ref}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const HeaderStandard = ({ title }: { title: string }) => (
    <div className="px-12 pt-8 pb-4 border-b border-slate-300 mb-6 flex justify-between items-center">
      <div className="flex items-center gap-3">
        <HumanRisksLogo className="h-5" variant="icon" />
        <h2 className="text-[16px] font-bold text-slate-500 uppercase tracking-tight leading-none">
          {title}
        </h2>
      </div>
      <div className="text-[8px] font-bold text-hr-pink uppercase tracking-widest">
        LHRGPA4 Specification
      </div>
    </div>
  );

  const Footer = ({ page, total }: { page: number; total: number }) => (
    <div className="absolute bottom-0 w-full px-12 pb-8 pt-4 bg-white z-10">
      <div className="border-t border-slate-200 flex justify-between items-center pt-4">
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
          Human Risks | Specialty Division of Lombard Insurance Company Ltd
        </span>
        <span className="text-[9px] font-bold text-slate-800 uppercase">
          Page {page.toString().padStart(2, '0')} of {total.toString().padStart(2, '0')}
        </span>
      </div>
    </div>
  );

  const formatComputationBasis = (cat: Category) => {
    if (cat.method === CalculationMethod.Stated) {
      return `${formatWageroll(cat.wageroll, currency)} (Wageroll)`;
    }
    return `${cat.headcount} Lives (Headcount)`;
  };

  const getRateDisplay = (cat: Category) => {
    const catTotal = cat.benefits.reduce(
      (acc: number, b: BenefitConfig) => acc + (b.netPremium || 0),
      0,
    );
    if (cat.method === CalculationMethod.Stated) {
      const rate = cat.wageroll > 0 ? (catTotal / cat.wageroll) * 100 : 0;
      return `${rate.toFixed(4)}%`;
    } else {
      const rate = cat.headcount > 0 ? catTotal / cat.headcount : 0;
      return `R ${rate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} pp`;
    }
  };

  // Exact list from Screenshot 3
  const extensions = [
    { name: 'Med Evacuation & Repat', limit: 'R 350,000' },
    { name: 'Mobility & Vehicle Mod', limit: 'R 250,000' },
    { name: 'Quadriplegia (Additional)', limit: '+25% Max R 1m' },
    { name: 'Accidental HIV Infection', limit: 'R 1,000,000' },
    { name: 'Rehabilitation & Retraining', limit: 'R 150,000' },
    { name: 'Paraplegia (Additional)', limit: '+10% Max R 500k' },
    { name: 'Blood Transfusion Costs', limit: 'R 100,000' },
    { name: 'Relocation Expenses', limit: 'R 150,000' },
    { name: 'Active Reservist Service', limit: 'R 1,000,000' },
    { name: 'Life Support Equipment', limit: 'R 100,000' },
    { name: 'Trauma Support Counselling', limit: 'R 25,000' },
    { name: 'Crime Extension (Add.)', limit: '10% Max R 50k' },
    { name: 'Hospital Cash Benefit', limit: 'R 2,000/Day' },
    { name: 'Childcare Attendance', limit: 'R 300/Day' },
    { name: 'Seat Belt Extension', limit: '10% Max R 100k' },
    { name: 'Any Reasonable Expense', limit: 'R 75,000' },
    { name: 'Temporary Driver Benefit', limit: 'R 25,000' },
    { name: 'Search & Rescue', limit: 'R 100,000' },
    { name: 'Terminal Expenses', limit: 'R 15,000' },
    { name: 'Unconsciousness Benefit', limit: 'Max R 3,500' },
    { name: 'Disappearance Warranty', limit: 'INCLUDED', highlight: true },
  ];

  const chunkArray = <T,>(arr: readonly T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const categoryChunks = chunkArray(context.categories, 4); // 4 categories per page (2x2 grid)
  const totalPages = 3 + categoryChunks.length;

  return (
    <div className="bg-slate-200 p-8 flex flex-col items-center gap-8 print:p-0 print:gap-0">
      <style>{`
        .pdf-page {
          --color-slate-50: #f8fafc;
          --color-slate-100: #f1f5f9;
          --color-slate-200: #e2e8f0;
          --color-slate-300: #cbd5e1;
          --color-slate-400: #94a3b8;
          --color-slate-500: #64748b;
          --color-slate-600: #475569;
          --color-slate-700: #334155;
          --color-slate-800: #1e293b;
          --color-slate-900: #0f172a;
          --color-hr-indigo: #1E1246;
          --color-hr-pink: #D31054;
          --color-white: #ffffff;
          --color-black: #000000;
          --color-pink-100: #fce7f3;
          --color-pink-200: #fbcfe8;
          --font-sans: "Inter", sans-serif;
        }
      `}</style>

      {/* --- PAGE 1: INSURED & LIMITS --- */}
      <div className={pageClass}>
        <HeaderPage1 />

        {/* Insured Profile - CLEAN LAYOUT */}
        <div className="px-12 mb-8">
          <div className="bg-hr-indigo px-6 py-2.5 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-4 bg-hr-pink rounded-full"></div>
              <span className="text-[10px] font-bold text-white uppercase tracking-[0.15em]">
                Insured & Intermediary Profile
              </span>
            </div>
          </div>
          <div className="border border-slate-200 border-t-0 rounded-b-lg bg-white overflow-hidden">
            {/* Row 1: Names */}
            <div className="flex border-b border-slate-100">
              <div className="w-3/5 p-4 border-r border-slate-100">
                <span className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  Proposed Insured Name
                </span>
                <div className="text-[16px] font-bold text-hr-indigo uppercase tracking-tight leading-tight">
                  {context.client_profile.companyName || 'PENDING NAME'}
                </div>
              </div>
              <div className="w-2/5 p-4">
                <span className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  Insurance Broker
                </span>
                <div className="text-[10px] font-bold text-slate-700 uppercase tracking-tight leading-tight">
                  {context.client_profile.brokerName}
                </div>
              </div>
            </div>
            {/* Row 2: Regulatory */}
            <div className="flex border-b border-slate-100">
              <div className="w-1/3 p-4 border-r border-slate-100">
                <span className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  VAT Number
                </span>
                <div className="text-[10px] font-semibold text-slate-800 tabular-nums">
                  {context.client_profile.vatNumber || 'N/A'}
                </div>
              </div>
              <div className="w-2/3 p-4">
                <span className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  Industry Sector
                </span>
                <div className="text-[10px] font-semibold text-slate-800 uppercase tracking-wide">
                  {context.client_profile.sector}
                </div>
              </div>
            </div>
            {/* Row 3: Physical & Web */}
            <div className="flex border-b border-slate-100">
              <div className="w-2/3 p-4 border-r border-slate-100">
                <span className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  Physical Address / Risk Address
                </span>
                <div className="text-[10px] font-medium text-slate-600 leading-relaxed">
                  {context.client_profile.address || 'Not Provided'}
                </div>
              </div>
              <div className="w-1/3 p-4">
                <span className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  Website
                </span>
                <div className="text-[10px] font-medium text-hr-pink underline decoration-hr-pink underline-offset-2">
                  {context.client_profile.website || 'N/A'}
                </div>
              </div>
            </div>
            {/* Row 4: Description */}
            <div className="p-4 bg-white">
              <span className="block text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                Business Description
              </span>
              <div className="text-[10px] text-slate-600 leading-relaxed text-justify font-medium italic line-clamp-6">
                &ldquo;{context.client_profile.businessDescription || 'No description provided.'}
                &rdquo;
              </div>
            </div>
          </div>
        </div>

        {/* Limits - SOPHISTICATED LAYOUT */}
        <div className="px-12 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-[0.15em]">
              Policy Liability Limits
            </h3>
            <div className="h-px bg-slate-100 flex-1"></div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="border border-slate-100 rounded-xl p-5 flex items-center justify-between bg-white shadow-sm border-l-4 border-l-hr-pink">
              <div>
                <div className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  Any One Life Limit (AOL)
                </div>
                <div className="text-xl font-bold text-hr-indigo tracking-tight">
                  {formatCurrency(context.policy_parameters.aolLimit, currency).replace('.00', '')}
                </div>
                <p className="text-[8px] font-semibold text-slate-400 uppercase mt-1.5 tracking-widest">
                  Per individual insured person
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-hr-pink">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            </div>

            <div className="border border-slate-100 rounded-xl p-5 flex items-center justify-between bg-white shadow-sm border-l-4 border-l-hr-pink">
              <div>
                <div className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  Any One Accident Limit (AOA)
                </div>
                <div className="text-xl font-bold text-hr-indigo tracking-tight">
                  {formatCurrency(context.policy_parameters.aoaLimit, currency).replace('.00', '')}
                </div>
                <p className="text-[8px] font-semibold text-slate-400 uppercase mt-1.5 tracking-widest">
                  Per single aggregate event
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-hr-pink">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <Footer page={1} total={totalPages} />
      </div>

      {/* --- PAGE 2+: DETAILED SCALES (DYNAMIC) --- */}
      {categoryChunks.map((chunk, chunkIdx) => (
        <div key={`scales-page-${chunkIdx}`} className={pageClass}>
          <HeaderStandard
            title={`Detailed Benefit Scales ${categoryChunks.length > 1 ? `(Part ${chunkIdx + 1})` : ''}`}
          />

          <div className="px-12 mb-6 flex-1">
            <div
              className={`grid ${chunk.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-x-8 gap-y-10`}
            >
              {chunk.map((cat) => {
                const rows: {
                  label: string;
                  value: string;
                  isSubItem?: boolean;
                  isNotIncluded?: boolean;
                }[] = [];
                const getBen = (t: BenefitType) => cat.benefits.find((b) => b.type === t);

                const deathBen = getBen(BenefitType.Death);
                if (deathBen) {
                  if (deathBen.isEnabled) {
                    rows.push({
                      label: 'Death',
                      value:
                        cat.method === CalculationMethod.Stated
                          ? `${deathBen.multiple}x Annual Earnings`
                          : formatCurrency(deathBen.sumInsured || 0, currency)
                              .replace('.00', '')
                              .replace(currency + ' ', currency + '\u00A0'),
                    });
                  } else {
                    rows.push({ label: 'Death', value: 'Not Included', isNotIncluded: true });
                  }
                }

                const ptdBen = getBen(BenefitType.PTD);
                if (ptdBen) {
                  if (ptdBen.isEnabled) {
                    rows.push({
                      label: 'Permanent Total Disability (PTD)',
                      value:
                        cat.method === CalculationMethod.Stated
                          ? `${ptdBen.multiple}x Annual Earnings`
                          : formatCurrency(ptdBen.sumInsured || 0, currency)
                              .replace('.00', '')
                              .replace(currency + ' ', currency + '\u00A0'),
                    });
                    rows.push({
                      label: 'Permanent Disability (PD)',
                      value: 'Table of Comp',
                      isSubItem: true,
                    });
                  } else {
                    rows.push({
                      label: 'Permanent Total Disability (PTD)',
                      value: 'Not Included',
                      isNotIncluded: true,
                    });
                    rows.push({
                      label: 'Permanent Disability (PD)',
                      value: 'Not Included',
                      isSubItem: true,
                      isNotIncluded: true,
                    });
                  }
                }

                const ttdBen = getBen(BenefitType.TTD);
                if (ttdBen) {
                  if (ttdBen.isEnabled) {
                    rows.push({
                      label: 'Temporary Total Disability (TTD)',
                      value:
                        cat.method === CalculationMethod.Stated
                          ? `${ttdBen.salaryPct}% Of Current Weekly Earnings, Max ${ttdBen.benefitPeriod} Weeks, ${ttdBen.excessDays} Day Excess`
                          : `Max ${formatCurrency(ttdBen.weeklyBenefit || 0, currency).replace(currency + ' ', currency + '\u00A0')} per Week, Max ${ttdBen.benefitPeriod} Weeks, ${ttdBen.excessDays} Day Excess`,
                    });
                    const tpdValue =
                      cat.method === CalculationMethod.Stated
                        ? `Included (40% of Weekly Earnings)`
                        : `Included (Max ${formatCurrency((ttdBen.weeklyBenefit || 0) * 0.4, currency).replace(currency + ' ', currency + '\u00A0')} per Week)`;
                    rows.push({
                      label: 'Temporary Partial Disability (TPD)',
                      value: tpdValue,
                      isSubItem: true,
                    });
                  } else {
                    rows.push({
                      label: 'Temporary Total Disability (TTD)',
                      value: 'Not Included',
                      isNotIncluded: true,
                    });
                    rows.push({
                      label: 'Temporary Partial Disability (TPD)',
                      value: 'Not Included',
                      isSubItem: true,
                      isNotIncluded: true,
                    });
                  }
                }

                const medBen = getBen(BenefitType.Medical);
                if (medBen) {
                  if (medBen.isEnabled) {
                    rows.push({
                      label: 'Emergency Medical Expenses (EME)',
                      value: `${formatCurrency(medBen.medicalLimit || 0, currency)
                        .replace('.00', '')
                        .replace(currency + ' ', currency + '\u00A0')} (Excess ${formatCurrency(
                        medBen.medicalExcess || 0,
                        currency,
                      )
                        .replace('.00', '')
                        .replace(currency + ' ', currency + '\u00A0')})`,
                    });
                  } else {
                    rows.push({
                      label: 'Emergency Medical Expenses (EME)',
                      value: 'Not Included',
                      isNotIncluded: true,
                    });
                  }
                }

                return (
                  <div
                    key={cat.id}
                    className="break-inside-avoid shadow-sm rounded-lg overflow-hidden border border-slate-100"
                  >
                    <div className="bg-hr-indigo px-5 py-3 flex justify-between items-center">
                      <span className="text-[10px] font-bold text-white uppercase tracking-wider">
                        {cat.name.toUpperCase()}
                      </span>
                      <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest border border-slate-500 rounded px-2 py-0.5">
                        {cat.method === CalculationMethod.Stated ? 'STATED' : 'FIXED'}
                      </span>
                    </div>
                    <div className="text-[10px]">
                      {rows.map((row, idx) => {
                        const bgClass = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                        return (
                          <div
                            key={idx}
                            className={`flex justify-between items-center px-5 py-3 border-b border-slate-100 last:border-0 ${bgClass} gap-4`}
                          >
                            <span
                              className={`font-bold w-1/2 ${row.isNotIncluded ? 'text-slate-300 italic' : 'text-slate-700'}`}
                            >
                              {row.label}
                            </span>
                            <span
                              className={`font-medium text-right w-1/2 whitespace-pre-line leading-snug ${row.isNotIncluded ? 'text-slate-300 italic' : 'text-hr-indigo font-bold'}`}
                            >
                              {row.value}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Footer page={2 + chunkIdx} total={totalPages} />
        </div>
      ))}

      {/* --- PAGE 3 (OR SHIFTED): PREMIUM COMPUTATION LEDGER --- */}
      <div className={pageClass}>
        <HeaderStandard title="Premium Computation Ledger" />

        <div className="px-12 flex-1 flex flex-col">
          <div className="mb-8">
            <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
              The following ledger provides a detailed breakdown of the annual premium calculations
              for each defined category of insured persons. Premiums are calculated based on the
              selected computation method (Stated Wageroll or Fixed Headcount) as specified in the
              proposal.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-8 border-t-4 border-t-hr-pink">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                <tr>
                  <th className="py-4 px-6 text-left text-[10px] font-bold uppercase tracking-[0.15em]">
                    Category Specification
                  </th>
                  <th className="py-4 px-6 text-left text-[10px] font-bold uppercase tracking-[0.15em]">
                    Computation Basis
                  </th>
                  <th className="py-4 px-6 text-center text-[10px] font-bold uppercase tracking-[0.15em]">
                    Applied Rate / PP
                  </th>
                  <th className="py-4 px-6 text-right text-[10px] font-bold uppercase tracking-[0.15em]">
                    Annual Premium
                  </th>
                </tr>
              </thead>
              <tbody className="text-[10px] text-slate-700">
                {context.categories.map((cat, i) => {
                  const catTotal = cat.benefits.reduce((acc, b) => acc + (b.netPremium || 0), 0);
                  return (
                    <tr
                      key={i}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                    >
                      <td className="py-4 px-6">
                        <div className="font-bold text-hr-indigo text-[10px] whitespace-nowrap">
                          {cat.name}
                        </div>
                        <div className="text-[8px] text-slate-400 font-bold uppercase mt-1">
                          Class Code: {cat.method === CalculationMethod.Stated ? 'ST-01' : 'FX-01'}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-slate-600 font-medium text-[10px]">
                        {formatComputationBasis(cat)}
                      </td>
                      <td className="py-4 px-6 text-center font-medium text-slate-600 text-[10px]">
                        {getRateDisplay(cat)}
                      </td>
                      <td className="py-4 px-6 text-right font-bold tabular-nums text-hr-indigo text-[14px]">
                        {formatCurrency(catTotal, currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Premium Summary Footer */}
            <div className="bg-slate-50 border-t border-slate-200 p-6 flex justify-between items-center">
              <div>
                <div className="text-[10px] font-bold text-hr-indigo uppercase tracking-[0.15em] mb-1">
                  Total Payable Annual Premium
                </div>
                <div className="text-[8px] font-semibold text-slate-500 uppercase tracking-widest">
                  Inclusive of Value Added Tax (VAT) @ 15%
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-hr-indigo tabular-nums tracking-tight leading-none">
                  {formatCurrency(context.pricing_summary.grossPremium, currency)}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Ledger Notes */}
          <div className="grid grid-cols-2 gap-8 mb-12">
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 border-l-4 border-l-hr-pink">
              <h4 className="text-[10px] font-black text-hr-indigo uppercase tracking-widest mb-2">
                Payment Terms
              </h4>
              <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                Premiums are payable annually in advance unless otherwise agreed in writing. Failure
                to remit premium within the statutory grace period may result in policy
                cancellation.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 border-l-4 border-l-hr-pink">
              <h4 className="text-[10px] font-black text-hr-indigo uppercase tracking-widest mb-2">
                Computation Audit
              </h4>
              <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                For Stated Wageroll policies, an annual audit of actual wageroll figures may be
                required at the end of the policy period to adjust the final premium.
              </p>
            </div>
          </div>
        </div>

        <Footer page={2 + categoryChunks.length} total={totalPages} />
      </div>

      {/* --- PAGE 4 (OR SHIFTED): FORMAL POLICY DETAILS --- */}
      <div className={pageClass}>
        <HeaderStandard title="Formal Policy Details" />

        {/* Specialist Assist Services */}
        <div className="px-12 mb-8">
          <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-widest mb-4 pb-2 border-b border-slate-100">
            Specialist Assist Services (LHRGPA4)
          </h3>

          <div className="grid grid-cols-5 gap-4">
            {[
              { label: 'Emergency Assist (MSO)', icon: 'plus' },
              { label: 'RAF Assist (Motor)', icon: 'car' },
              { label: 'COID Assist (IOD)', icon: 'briefcase' },
              { label: 'MED Assist (Specialist)', icon: 'stethoscope' },
              { label: 'INVEST Assist (Advice)', icon: 'chart' },
            ].map((srv, i) => (
              <div
                key={i}
                className="border-2 border-pink-200 rounded-xl p-4 flex flex-col items-center justify-center text-center h-28 bg-pink-100 shadow-sm"
              >
                <div className="text-hr-pink mb-2">
                  {srv.icon === 'plus' && (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  )}
                  {srv.icon === 'car' && (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                      />
                    </svg>
                  )}
                  {!['plus', 'car'].includes(srv.icon) && (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                </div>
                <span className="text-[10px] font-black text-hr-indigo uppercase tracking-wide leading-tight">
                  {srv.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Coverage Extension Registry - EXACT LIST */}
        <div className="px-12 mb-8">
          <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-widest mb-4 pb-2 border-b border-slate-100">
            Coverage Extension Registry
          </h3>

          <div className="grid grid-cols-3 gap-x-8 gap-y-3">
            {extensions.map((ext, i) => (
              <div
                key={i}
                className="flex justify-between items-center border-b border-dotted border-slate-200 pb-1.5"
              >
                <span
                  className={`text-[9px] font-medium ${ext.highlight ? 'text-hr-pink font-bold' : 'text-slate-600'}`}
                >
                  {ext.name}
                </span>
                <span
                  className={`text-[9px] font-bold ${ext.highlight ? 'text-hr-pink' : 'text-slate-800'}`}
                >
                  {ext.limit}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* NEW: EXTERNAL QUOTE NOTES SECTION (VISIBLE ON PDF) */}
        {context.client_profile.quoteNotes && (
          <div className="px-12 mb-8">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 border-l-4 border-l-hr-pink">
              <h3 className="text-[10px] font-bold text-hr-indigo uppercase tracking-widest mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Specific Quote Conditions
              </h3>
              <div className="text-[10px] text-slate-700 whitespace-pre-line leading-relaxed font-medium">
                {context.client_profile.quoteNotes}
              </div>
            </div>
          </div>
        )}

        {/* Global Crisis Response Unit - CLEAN CARD */}
        <div className="px-12 mb-8">
          <div className="bg-[#1E1246] rounded-xl p-6 relative overflow-hidden flex justify-between items-center shadow-sm">
            <div className="relative z-10">
              <div className="text-[9px] font-bold text-hr-pink uppercase tracking-widest mb-1.5">
                Global Crisis Response Unit (24/7)
              </div>
              <div className="text-xl font-bold text-white tracking-tight mb-1.5">
                +27 11 259 5462
              </div>
              <div className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.1em]">
                Integrated Case Management & Specialist Evacuation
              </div>
            </div>

            {/* Phone Icon Circle */}
            <div className="w-12 h-12 bg-hr-pink rounded-full flex items-center justify-center text-white shadow relative z-10">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Formal Provisions */}
        <div className="px-12 mb-8">
          <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-widest mb-4">
            Formal Provisions
          </h3>
          <div className="grid grid-cols-2 gap-8 text-[10px] leading-relaxed text-slate-600">
            <div>
              <strong className="text-slate-800 block mb-1.5">WORDING STANDARD:</strong>
              Subject to the terms of the{' '}
              <span className="text-hr-pink font-bold">LHRGPA4 Policy Wording</span>. Alcohol
              exclusion applied where BAC &gt; 0.1g/100ml.
            </div>
            <div>
              <strong className="text-slate-800 block mb-1.5">DISCLOSURE DUTY:</strong>
              Insured must disclose all material facts to ensure the validity of the contract.
            </div>
          </div>
        </div>

        <Footer page={3 + categoryChunks.length} total={totalPages} />
      </div>
    </div>
  );
};
